/**
 * Who is here, crossed by two of the room's own axes. The axes come from the
 * frame, never from code; pick any two. Empty cells are the point.
 */
import { useState } from 'react';
import { CARD } from './tableLayout';
import type { BoardroomLandscape, BoardroomLandscapeRow, BoardroomLandscapeSection } from '../../../types';
import { KIND_LABELS } from '../kinds';
import MissingList, { type OnDig } from './MissingList';
import { VerdictBadge } from '../ItemCard';
import DataTable from './DataTable';
import type { Column } from './tableColumns';
import { useReportSeen } from '../useSeen';
import NewHere from './NewHere';

const nice = (d: string) => d.toLowerCase().replace(/_/g, ' ');


export default function LandscapeReport({
  data,
  onOpen,
  onPickTag,
  onDig,
  digging,
  unseen,
  onSeen,
  roomName,
}: {
  /** Open a player's profile. */
  onOpen: (itemId: number) => void;
  data: BoardroomLandscape;
  /** Narrow the room to one value: "DIM:value". */
  onPickTag?: (key: string) => void;
  onDig: OnDig;
  digging: boolean;
  /** Ids that changed and have not been looked at. */
  unseen?: ReadonlySet<number>;
  /** Ids now on screen — the caller drops them from the badge. */
  onSeen?: (ids: number[]) => void;
  /** For the download filename, so a saved table says which room it is. */
  roomName?: string;
}) {
  const empty = data.totals.players === 0 || data.axes.length === 0;
  // WHICH LIST IS OPEN: a row's players (all of them) or one cell's. A count is
  // a question — "who?" — and the answer belongs under the row.
  //
  // KEYED BY THE AXIS TOO. The report draws a section per axis and `expanded`
  // is asked in every one of them, so a row identified by its value alone is
  // whichever section got there first: two axes that share a value string —
  // and nothing stops them — meant clicking that row in the second section
  // silently CLOSED the one open in the first, and any section that expanded
  // on another's key drew an empty list, because `players` filters on a column
  // belonging to an axis it is not crossed by.
  const [openList, setOpenList] = useState<{ axis: string; row: string; col: string | null } | null>(null);
  const toggle = (axis: string, row: string, col: string | null) =>
    setOpenList(cur =>
      cur && cur.axis === axis && cur.row === row && cur.col === col ? null : { axis, row, col },
    );
  const isOpen = (axis: string, row: string) => openList?.axis === axis && openList.row === row;
  // THE MAP IS NOT A TABLE OF ROWS. Its rows are axis values that persist
  // across digs; what actually arrives is a PLAYER landing in a cell. So the
  // marks are on the players, and a row carries one while any of its own are
  // unseen — otherwise the only sign of an arrival is a count going up, and
  // the chips that changed sit behind a collapsed cell.
  //
  // EVERY SECTION IT DRAWS, not the chosen pair. `data.rows` is the crossing
  // the axis buttons point at; the report renders a section per axis the frame
  // offers. Counting only `rows` meant a player placed on `deployment` but not
  // on `buyer` was pruned as "no row here" the moment the tab opened — a mark
  // dropped for work you never saw — and, before that, sat on the badge
  // against a table that showed 122 of 279 players.
  const seen = useReportSeen(unseen, onSeen, [
    ...(data.sections ?? []).flatMap(s => s.rows.flatMap(r => r.players.map(p => p.id))),
    ...data.missing.map(m => m.item_id),
  ]);
  const rowCls = (r: { players: { id: number }[] }): string =>
    r.players.map(p => seen.cls(p.id)).find(Boolean) ?? '';

  /** ONE DESCRIPTION PER COLUMN, for a grid whose columns come from the data:
   *  the two counts, then one per value of the crossing axis. Written as
   *  functions of the row, so the same list draws the table, fills the file,
   *  feeds the picker — and transposes when the reader flips it. */
  const sectionColumns = (sec: BoardroomLandscapeSection): Column<BoardroomLandscapeRow>[] => [
    {
      key: 'value',
      label: nice(sec.a),
      // Flipped, the values run across and the crossing axis comes down.
      flipLabel: sec.b ? nice(sec.b) : 'value',
      always: true,
      value: r => r.value,
      cell: r => (
        <button
          type="button"
          onClick={() => onPickTag?.(`${sec.a}:${r.value}`)}
          className="text-xs text-gray-800 dark:text-gray-200 hover:underline text-left"
        >
          {r.value}
        </button>
      ),
    },
    {
      key: 'players',
      label: 'players',
      align: 'right',
      value: r => r.total,
      cellClass: () => 'font-mono text-gray-700 dark:text-gray-200 pr-3',
      cell: r => (
        <button type="button" onClick={() => toggle(sec.a, r.value, null)} className="hover:underline" title="Who?" data-testid={`players-${r.value}`}>
          {r.total}
        </button>
      ),
    },
    {
      key: 'confident',
      label: 'confident',
      align: 'right',
      value: r => r.confident || '',
      cellClass: () => 'font-mono text-emerald-600 pr-3',
    },
    ...sec.cols.map(c => ({
      key: `col:${c}`,
      label: c,
      vertical: true,
      value: (r: BoardroomLandscapeRow) => r.cells[c] ?? 0,
      cellId: (r: BoardroomLandscapeRow) => `cell-${r.value}-${c}`,
      cellClass: (r: BoardroomLandscapeRow) =>
        `text-center px-1 font-mono ${r.cells[c] ? 'text-gray-800 dark:text-gray-200' : 'text-rose-300 dark:text-rose-700'}`,
      head: (
        <button
          type="button"
          onClick={() => onPickTag?.(`${sec.b}:${c}`)}
          className="text-[11px] whitespace-nowrap text-gray-500 dark:text-gray-400 hover:underline"
        >
          {c.length > 22 ? c.slice(0, 21) + '…' : c}
        </button>
      ),
      cell: (r: BoardroomLandscapeRow) =>
        r.cells[c] ? (
          <button type="button" onClick={() => toggle(sec.a, r.value, c)} className="hover:underline" title={`Who is ${r.value} × ${c}?`}>
            {r.cells[c]}
          </button>
        ) : (
          '·'
        ),
    })),
  ];

  /** WHO IS IN THIS ROW. A count is a question and this is the answer, under
   *  the row that asked it. */
  const players = (sec: BoardroomLandscapeSection, r: BoardroomLandscapeRow) => {
    // The column narrows this list only when it is THIS section's column; a
    // section drawing on another's key would filter on an axis it is not
    // crossed by and show nobody.
    const col = isOpen(sec.a, r.value) ? openList?.col ?? null : null;
    return (
      <div data-testid={`players-list-${r.value}`}>
        <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
          {r.value}
          {col ? ` × ${col}` : ''} · {r.players.filter(p => !col || p.b === col).length}
        </div>
        <ul className="flex flex-wrap gap-1">
          {r.players
            .filter(p => !col || p.b === col)
            .map(p => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onOpen(p.id)}
                  title={`${KIND_LABELS[p.kind]}${p.b ? ` · ${p.b}` : ''}`}
                  data-testid={`player-${p.id}`}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-xs hover:border-indigo-300 dark:hover:border-indigo-700 ${seen.cls(p.id)} ${
                    // An IDEA on the map is a marker, not a player: dashed, in
                    // the ideas' colour, so a cell it fills alone reads as a gap
                    // the room could take rather than one already taken.
                    p.kind === 'IDEA'
                      ? 'border-dashed border-violet-400 dark:border-violet-500 text-violet-800 dark:text-violet-200'
                      : 'border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200'
                  }`}
                >
                  {p.title}
                  {p.verdict && <VerdictBadge verdict={p.verdict} />}
                </button>
              </li>
            ))}
        </ul>
      </div>
    );
  };

  /** One axis down the side, crossed by another.
   *
   *  The frame usually names four or five and they are the same players
   *  counted a different way. Reading them meant clicking a button per axis
   *  and holding the last one in your head — so they stack and scroll, the way
   *  every other report shows its groups. */
  const Section = ({ sec }: { sec: BoardroomLandscapeSection }) => (
    <section className="shrink-0">
      <div className="flex items-baseline gap-2 mb-1 px-1">
        {/* The axis IS the table's name — as dark as the group titles on every
            other report, not a caption. */}
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-900 dark:text-gray-100">
          {nice(sec.a)}
        </h3>
        <span className="text-[11px] text-gray-400">
          {sec.rows.length} value{sec.rows.length === 1 ? '' : 's'}
          {sec.b ? ` × ${nice(sec.b)}` : ''}
          {sec.unplaced ? ` · ${sec.unplaced} not placed on it` : ''}
        </span>
      </div>
      <div className={CARD}>
        <DataTable
          table={`landscape-${sec.a}`}
          file={`landscape-${sec.a}`}
          room={roomName}
          columns={sectionColumns(sec)}
          rows={sec.rows}
          rowKey={r => `landscape-row-${r.value}`}
          testId={`landscape-${sec.a}`}
          // A column per value of the crossing axis: no room to declare widths.
          defaultMode="long"
          verticalHeads
          expanded={r => isOpen(sec.a, r.value)}
          detail={r => players(sec, r)}
          // THE ROW STANDS FOR ITS PLAYERS, and the marks are on the players:
          // its chips sit behind a collapsed cell, so waiting for one to be on
          // screen meant waiting for a click that never came. One tracker for
          // the whole report, because a tracker per section would each prune
          // the others' marks as ids it has no row for.
          //
          // The axis and the value NAME the row, because the same player is a
          // member of a row in every section: keyed by its players alone, two
          // sections' rows collided and one stopped being watched.
          rowRef={r => seen.markGroup(r.players.map(p => p.id), `${sec.a}:${r.value}`)}
          rowClass={rowCls}
        />
      </div>
      {/* THE HOLES IN THIS TABLE, under this table. One list at the foot of the
          page named the empty cells of whichever crossing the row picker
          happened to point at — so it described one section out of five, and
          the reader had no way to tell which. Each section owns its own. */}
      {sec.gaps.length > 0 && sec.b && (
        <div className="mt-1 px-1 text-xs text-gray-500 dark:text-gray-400" data-testid={`gaps-${sec.a}`}>
          <span className="text-rose-600 dark:text-rose-400">·</span> nobody here:{' '}
          {sec.gaps.slice(0, 12).map((g, i) => (
            <span key={`${g.a}|${g.b}`}>
              {i > 0 && ', '}
              {g.a} × {g.b}
            </span>
          ))}
          {sec.gaps.length > 12 && ` and ${sec.gaps.length - 12} more`}
        </div>
      )}
    </section>
  );

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <NewHere count={seen.waitingHere} glowing={seen.glowing} onJump={seen.next} onClearAll={seen.readAll} />
      {!empty && (data.sections ?? []).map(sec => <Section key={sec.a} sec={sec} />)}
      {empty && data.axes.length === 0 && data.missing.length === 0 && <div className="text-xs text-gray-400 py-4">Nothing tagged on the room's axes yet.</div>}
      <MissingList missing={data.missing} empty={empty} onOpen={onOpen} onDig={onDig} digging={digging} seen={seen} />
    </div>
  );
}
